/**
 * Data layer for `android-app-links`. Thin typed fetch wrappers over
 * /api/v1/android-app-links. Raw `fetch()` is confined to this file per the
 * dash contract (D6). Mirror of BE route `apps/api/src/routes/android-app-links.ts`.
 */

export type VerificationStatus = "pending" | "verified" | "failed";

export interface AndroidAppLinkView {
	id: string;
	host: string;
	package_name: string;
	fingerprints: string[];
	label: string;
	verification_status: VerificationStatus;
	last_checked_at: number | null;
	enabled: boolean;
	created_at: number;
	updated_at: number;
}

export interface ListAndroidAppLinksResponse {
	rows: AndroidAppLinkView[];
	host?: string;
}

export interface CreateAndroidAppLinksBody {
	host: string;
	packageName: string;
	fingerprints: string[];
	label?: string;
}

export interface PatchAndroidAppLinksBody {
	label?: string;
	fingerprints?: string[];
	enabled?: boolean;
	verification_status?: VerificationStatus;
	last_checked_at?: number | null;
}

const BASE = "/api/v1/android-app-links";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(host?: string): Promise<ListAndroidAppLinksResponse> {
	const qs = host ? `?host=${encodeURIComponent(host)}` : "";
	const res = await fetch(`${BASE}${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAndroidAppLinksResponse>(res);
}

export async function create(body: CreateAndroidAppLinksBody): Promise<AndroidAppLinkView> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AndroidAppLinkView>(res);
}

export async function patch(
	id: string,
	body: PatchAndroidAppLinksBody,
): Promise<AndroidAppLinkView> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AndroidAppLinkView>(res);
}

export async function remove(id: string): Promise<{ ok: true; id: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: true; id: string }>(res);
}

export const androidAppLinksQueryKeys = {
	all: ["android-app-links"] as const,
	list: (host?: string) =>
		host
			? (["android-app-links", "list", host] as const)
			: (["android-app-links", "list"] as const),
	detail: (id: string) => ["android-app-links", id] as const,
};
